import React from 'react';

export interface PricingPlan {
  id: string;
  name: string;
  price: number;
  interval: 'month' | 'year';
  features: string[];
  popular?: boolean;
  stripePriceId?: string;
}

export interface SubscriptionPlansProps {
  plans: PricingPlan[];
  onPlanSelect: (planId: string) => void;
  currentPlan?: string;
  loading?: boolean;
  /** Enable dark mode colors (default: false for backward compat) */
  darkMode?: boolean;
  className?: string;
}

export const SubscriptionPlans: React.FC<SubscriptionPlansProps> = ({
  plans,
  onPlanSelect,
  currentPlan,
  loading = false,
  darkMode = false,
  className = '',
}) => {
  const formatPrice = (price: number, interval: string) => {
    return `$${price}/${interval}`;
  };

  // Color palette swaps based on dark mode
  const c = darkMode
    ? {
        cardBg: '#1f2937',
        cardBorder: '#374151',
        text: '#f9fafb',
        textMuted: '#9ca3af',
        featureBorder: '#374151',
        currentBg: '#064e3b',
        accent: '#60a5fa',
        currentAccent: '#34d399',
      }
    : {
        cardBg: 'white',
        cardBorder: '#e5e7eb',
        text: '#1f2937',
        textMuted: '#6b7280',
        featureBorder: '#f3f4f6',
        currentBg: '#f0fdf4',
        accent: '#3b82f6',
        currentAccent: '#10b981',
      };

  const styles = {
    container: {
      width: '100%',
    } as React.CSSProperties,
    plansGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
      gap: '2rem',
      margin: '2rem 0',
    } as React.CSSProperties,
    planCard: {
      border: `2px solid ${c.cardBorder}`,
      borderRadius: '12px',
      padding: '2rem',
      position: 'relative',
      background: c.cardBg,
      transition: 'all 0.3s ease',
    } as React.CSSProperties,
    planCardPopular: {
      borderColor: c.accent,
      boxShadow: `0 10px 25px ${darkMode ? 'rgba(96, 165, 250, 0.15)' : 'rgba(59, 130, 246, 0.1)'}`,
    } as React.CSSProperties,
    planCardCurrent: {
      borderColor: c.currentAccent,
      background: c.currentBg,
    } as React.CSSProperties,
    popularBadge: {
      position: 'absolute',
      top: '-12px',
      left: '50%',
      transform: 'translateX(-50%)',
      background: c.accent,
      color: 'white',
      padding: '0.5rem 1rem',
      borderRadius: '20px',
      fontSize: '0.875rem',
      fontWeight: 600,
    } as React.CSSProperties,
    planHeader: {
      textAlign: 'center',
      marginBottom: '2rem',
    } as React.CSSProperties,
    planName: {
      fontSize: '1.5rem',
      fontWeight: 700,
      marginBottom: '0.5rem',
      color: c.text,
    } as React.CSSProperties,
    planPrice: {
      fontSize: '2rem',
      fontWeight: 800,
      color: c.accent,
    } as React.CSSProperties,
    planFeatures: {
      marginBottom: '2rem',
    } as React.CSSProperties,
    featuresList: {
      listStyle: 'none',
      padding: 0,
      margin: 0,
    } as React.CSSProperties,
    featureItem: {
      padding: '0.5rem 0',
      borderBottom: `1px solid ${c.featureBorder}`,
      color: c.textMuted,
    } as React.CSSProperties,
    planButton: {
      width: '100%',
      padding: '1rem',
      border: 'none',
      borderRadius: '8px',
      fontWeight: 600,
      fontSize: '1rem',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      background: c.accent,
      color: 'white',
    } as React.CSSProperties,
    planButtonCurrent: {
      background: c.currentAccent,
    } as React.CSSProperties,
    planButtonDisabled: {
      opacity: 0.6,
      cursor: 'not-allowed',
    } as React.CSSProperties,
  };

  return (
    <div
      style={styles.container}
      className={className}
      role="region"
      aria-label="Pricing plans"
    >
      <div style={styles.plansGrid} role="list">
        {plans.map(plan => (
          <div
            key={plan.id}
            role="listitem"
            aria-label={`${plan.name} plan - ${formatPrice(plan.price, plan.interval)}`}
            aria-current={currentPlan === plan.id ? 'true' : undefined}
            style={{
              ...styles.planCard,
              ...(plan.popular ? styles.planCardPopular : {}),
              ...(currentPlan === plan.id ? styles.planCardCurrent : {}),
            }}
          >
            {plan.popular && (
              <div style={styles.popularBadge} aria-label="Most popular plan">
                Most Popular
              </div>
            )}

            <div style={styles.planHeader}>
              <h3 style={styles.planName}>{plan.name}</h3>
              <div
                style={styles.planPrice}
                aria-label={`${formatPrice(plan.price, plan.interval)} per ${plan.interval}`}
              >
                {formatPrice(plan.price, plan.interval)}
              </div>
            </div>

            <div style={styles.planFeatures}>
              <ul
                style={styles.featuresList}
                role="list"
                aria-label={`${plan.name} features`}
              >
                {plan.features.map((feature, index) => (
                  <li key={index} style={styles.featureItem}>
                    <span aria-hidden="true">✓ </span>
                    {feature}
                  </li>
                ))}
              </ul>
            </div>

            <button
              style={{
                ...styles.planButton,
                ...(currentPlan === plan.id ? styles.planButtonCurrent : {}),
                ...(loading || currentPlan === plan.id
                  ? styles.planButtonDisabled
                  : {}),
                minHeight: '44px',
              }}
              onClick={() => onPlanSelect(plan.id)}
              disabled={loading || currentPlan === plan.id}
              aria-label={
                currentPlan === plan.id
                  ? `${plan.name} is your current plan`
                  : `Select ${plan.name} plan`
              }
            >
              {loading
                ? 'Loading...'
                : currentPlan === plan.id
                  ? 'Current Plan'
                  : 'Select Plan'}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};
